import { type Database } from "@fitness-app/supabase";

import { type TrainingFeedback } from "./Measurement";
import { type ProgramWorkout } from "./TrainingProgram";

export type ClientWorkoutActivity = Database["public"]["Tables"]["client_workout_activity"]["Row"];

export enum ActivityEventType {
  Workout = "workout",
}

export interface WorkoutActivityHealthExportMetadata {
  energyBurnedKcal?: number | null;
  error?: string | null;
  exportedAt?: string | null;
  externalId?: string | null;
  failedAt?: string | null;
  provider?: string | null;
  reason?: string | null;
  recordIds?: string[];
  requestedAt?: string | null;
  skippedAt?: string | null;
  status: "pending" | "exported" | "skipped" | "failed";
}

export interface WorkoutActivityHealthLinkMetadata {
  externalId: string;
  heartRateAverageBpm?: number | null;
  heartRateMaxBpm?: number | null;
  heartRateMinBpm?: number | null;
  heartRateSamples?: number;
  /** Active energy the source measured for the workout. */
  energyBurnedKcal?: number | null;
  linkedAt: string;
  provider: string;
  /** "watch": recorded by the SylwetkaPro Apple Watch app and linked on its own (TRE-2244). */
  source?: "manual" | "watch";
  /** Stored as-is from the device; older rows may lack fields, so read it through a guard. */
  snapshot?: unknown;
}

/** Shape the mobile app writes into `linkedWorkout.snapshot` (its `HealthWorkoutSnapshot`). */
export interface WorkoutActivityHealthLinkSnapshot {
  title: string | null;
  activityType: string | null;
  startDate: string;
  endDate: string;
  durationSeconds: number;
  totalEnergyBurnedKcal: number | null;
  totalDistanceMeters: number | null;
  heartRateSamples: { startDate: string; endDate: string; bpm: number }[];
  sourceName?: string | null;
}

export interface WorkoutActivityHealthMetadata {
  export?: WorkoutActivityHealthExportMetadata;
  linkedWorkout?: WorkoutActivityHealthLinkMetadata;
}

export interface ActivityEvent<T extends object> extends Omit<ClientWorkoutActivity, "data" | "metadata"> {
  id: string;
  type: ActivityEventType;
  createdAt: string;
  updatedAt: string;
  clientEmail: string;
  traineeId: string;
  userId: string | null;
  data: T;
  eventDate: string;
  eventMonth: string;
  eventWeek: string;
  eventTimestamp: string;
  metadata:
    | null
    | (Record<string, unknown> & {
        health?: WorkoutActivityHealthMetadata;
        source?: "firestore" | "postgres";
        lastSyncedAt?: number;
      });
}

export interface WorkoutActivityEvent extends ActivityEvent<ProgramWorkout> {
  traineeId: string;
  programId: string | null;
  workoutId: string;
  type: ActivityEventType.Workout;
}

export interface WorkoutActivityEventWithFeedback extends WorkoutActivityEvent {
  workoutRate?: null | TrainingFeedback["data"];
}

export type ProgramActivity = Record<string, WorkoutActivityEventWithFeedback[]>;
